import React, { Dispatch, SetStateAction, use, useCallback, useMemo, useState } from 'react';
import { noop } from '@fabric-ui/utils';
import { useUserActivityStatus } from '@hooks/auth';
import { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { shouldSkipUILogin } from '@utils/auth.utils';
import { AuthStorageKeys } from '@utils/localStorageAuthUtils.utils';
import { useSnackbar } from 'notistack';
import { AuthStateType, User } from './auth/auth.types';
import { usePermissions } from './context/AvContext.hooks';
import { AvPermissionsType } from './context/AvContext.types';
import { client } from './context/AvQueryProvider';
import { useNetworkStatus } from './context/NetworkStatusContext';

export const REDIRECT_MESSAGES = {
  YOU_DONT_HAVE_ACCESS_TO_THIS_PAGE: "You don't have access to this page",
  YOU_DONT_HAVE_ACCESS_TO_THIS_APP: "You don't have access to this app",
};
const getAuthConfig = () => JSON.parse(localStorage.getItem(AuthStorageKeys.authConfig) || '{}');

export type RouterContextType = {
  setAuthState: Dispatch<SetStateAction<AuthStateType>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  userIsActive: boolean;
  featureFlags: Record<string, string>;
  setFeatureFlags: Dispatch<SetStateAction<Record<string, string>>>;
  userPermissions: AvPermissionsType;
  getAuthConfig: typeof getAuthConfig;
  isOnline: boolean;
  queryClient: QueryClient;
  validatePermissions: ({ isAllowed, reason, preload }: { isAllowed: boolean; reason?: string; preload?: boolean }) => void;
  validateAppPermission: (appPath: string, preload: boolean) => void;
} & AuthStateType;

export const defaultUser: User = {
  accountId: '',
  roleId: '',
  userId: '',
  email: '',
  userName: '',
  permissions: '',
  accountApps: [],
};

export const routerContextDefaultValues = {
  user: defaultUser,
  isAuthenticated: shouldSkipUILogin(),
  setAuthState: noop,
  isLoading: true,
  setIsLoading: noop,
  userIsActive: true,
  featureFlags: {},
  setFeatureFlags: noop,
  userPermissions: {
    hasAllowedPermission: () => false,
    hasAllowedPermissionToResource: () => false,
    hasAllowedEditResourcePermission: () => false,
    allowedPermissionsToResource: () => ({}),
    allowedPermissionsToPath: () => ({}),
    hasAppInAccount: () => false,
    hasOnlyPermission: () => false,
    allowedPermissions: {},
    isInternalRole: false,
    isAvalorInternalRole: false,
    isAvalorAdmin: false,
    isBackOfficeAvailable: false,
  },
  getAuthConfig,
  isOnline: navigator.onLine,
  queryClient: client,
  validatePermissions: noop,
  validateAppPermission: noop,
};

export const RouterContext = React.createContext<RouterContextType>(routerContextDefaultValues);

export const RouterContextProvider = ({ children }) => {
  const [{ user, isAuthenticated }, setAuthState] = useState<AuthStateType>({
    user: routerContextDefaultValues.user,
    isAuthenticated: routerContextDefaultValues.isAuthenticated,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState({});
  const userPermissions = usePermissions({ featureFlags, user });
  const userIsActive = useUserActivityStatus();
  const { isOnline } = useNetworkStatus();
  const { enqueueSnackbar } = useSnackbar();
  const validatePermissions = useCallback(
    ({ isAllowed, reason, preload = false }: { isAllowed: boolean; reason?: string; preload?: boolean }): void => {
      if (!preload && !isAllowed && !isLoading && isAuthenticated && Object.keys(featureFlags).length > 0) {
        const homePage = `/${user.accountId ?? ''}`;
        const displayReason = reason ?? REDIRECT_MESSAGES.YOU_DONT_HAVE_ACCESS_TO_THIS_PAGE;
        enqueueSnackbar(displayReason, { variant: 'warning' });
        throw redirect({ to: homePage });
      }
    },
    [user.accountId, isLoading, isAuthenticated, featureFlags]
  );

  const validateAppPermission = useCallback(
    (appPath: string, preload: boolean = false): void => {
      validatePermissions({
        isAllowed: userPermissions.hasAppInAccount({ appPath }),
        preload,
        reason: REDIRECT_MESSAGES.YOU_DONT_HAVE_ACCESS_TO_THIS_APP,
      });
    },
    [validatePermissions, userPermissions]
  );

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      setAuthState,
      isLoading,
      userIsActive,
      setIsLoading,
      featureFlags,
      setFeatureFlags,
      userPermissions,
      getAuthConfig,
      isOnline,
      queryClient: client,
      validatePermissions,
      validateAppPermission,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      featureFlags,
      userPermissions,
      userIsActive,
      isOnline,
      client,
      validatePermissions,
      validateAppPermission,
    ]
  );

  return <RouterContext value={value}>{children}</RouterContext>;
};

export const useRouterContext = () => use(RouterContext);
